<template>
  <div class="strategy-play-btn">
    <a-tooltip v-if="isTurnedOn && !loadingPlayAux && !loadingPlay">
      <template
        v-if="
          !loadingPlay &&
          showTooltipSms() &&
          strategy.remaining_phones < strategy.total_phones
        "
        #title
      >
        Se ha pausado la estrategia <br />
        por falta de saldo.
      </template>
      <a-button block type="text" @click="handleFinishStrategy" class="table-cell__actions-btn">
        <template #icon>
          <PauseOutlined />
        </template>
      </a-button>
    </a-tooltip>

    <a-popover v-else-if="isTtsGenerating" placement="left">
      <template #content>
        <div class="tts-generation-popover">
          <h4>Generando {{ formattedTtsTotalRecords }} audios</h4>
          <p>Quedan {{ formattedTtsPendingRecords }} por generar</p>

          <div class="tts-generation-progress">
            <a-progress
              :percent="ttsGenerationPercent"
              :show-info="false"
              size="small"
            />
            <span>{{ ttsGenerationPercent }}%</span>
          </div>

          <p v-if="formattedTtsEstimatedTimeLeft" class="tts-generation-time">
            Tiempo estimado: {{ formattedTtsEstimatedTimeLeft }}
          </p>
        </div>
      </template>

      <a-button block type="text" class="table-cell__actions-btn">
        <a-button block type="text" class="table-cell__actions-btn tts-generating-btn">
          <span class="tts-generating-spinner"></span>
        </a-button>
      </a-button>
    </a-popover>

    <a-tooltip v-else-if="showGenerateTtsAudiosButton">
      <template #title>
        Generar audios para poder lanzar esta estrategia.
      </template>
      <a-button
        block
        type="text"
        class="table-cell__actions-btn"
        @click="handleOpenGenerateAudiosModal"
      >
        <template #icon>
          <SyncTTSAudiosIcon />
        </template>
      </a-button>
    </a-tooltip>

    <a-tooltip v-else>
      <template v-if="readyToPlay &&
            showTooltipSms() && strategy.remaining_phones == 0" #title>
        No cuenta con salgo suficiente <br />
        para realizar lanzar esta estrategia.
      </template>
      <template v-if="readyToPlay &&
            showTooltipSms() && strategy.remaining_phones > 0" #title>
        Se ha pausado la estrategia <br />
        por falta de saldo.
      </template>
      <template v-else-if="readyToPlay && (loadingPlay || loadingPlayAux)" #title>
        <span v-if="waitingToPause">
          {{ loadingPlayAux ? 
            'Estrategia en progreso. En unos breves segundos podrás pausar la estrategia.' : 
            'La estrategia está iniciando' }} 
        </span>
        <span v-else-if="waitingToPlay">
          {{ loadingPlayAux ? 
            'Estrategia pausada. En unos breves segundos podrás reanudar la estrategia.' : 
            'La estrategia está pausandose' }}
        </span>
      </template>
      <a-tooltip>
        <template v-if="!portfolioHasDialingPlan && !isEmail" #title>
          La campaña de esta estrategia no tiene un plan de marcación asignado
        </template>
        <template v-else-if="showNCEquivalenciesWarning && showAddAgentsWarning" #title>
          Configura el equivalente del tipo de grupo “No contacto” de tu campaña
          <router-link :to="`/operations/campaigns/${strategy?.portfolio?.id}`">aquí,</router-link>
          y agrega usuarios para poder comenzar tu estrategia.
        </template>
        <template v-else-if="showNCEquivalenciesWarning" #title>
          Para poder comenzar tu estrategia, configura el equivalente del tipo de grupo “No
          contacto” de tu campaña
          <router-link :to="`/operations/campaigns/${strategy?.portfolio?.id}`">aquí.</router-link>
        </template>
        <template v-else-if="isPredictive && scheduleNotExecuted" #title>
          La estrategia no se pudo iniciar en el horario programado. Edita el nuevo horario de inicio.
        </template>
        <template v-else-if="(isEmail || (isPredictive && isProgramed)) && showEmailTime !== 0" #title>
          <span v-if="isEmail">
            Estrategia programada para el {{ showEmailTime }}. Puede <br />
            cambiar su configuración editando su hora de envío.
          </span>
          <span v-else>
            La estrategia está programada para iniciar el {{ dayjs(showEmailTime).format("DD/MM/YYYY [a las] HH:mm") }}. <br />
            <!-- <span v-if="showAddAgentsWarning">Debes asignar usuarios a tu estrategia.</span> -->
          </span>
        </template>
        <template v-else-if="showAddAgentsWarning && !isEmail" #title>
          Agrega usuarios para poder <br />
          comenzar tu estrategia.
        </template>
        <!-- Hidden IVR for PoC -->
        <!-- <template v-else-if="showSetupIvrWarning" #title>
          Configura tu IVR para poder <br />
          comenzar tu estrategia.
        </template> -->
        <template v-else-if="showSetupQueuesWarning" #title>
          Configura colas para poder <br />
          comenzar tu estrategia.
        </template>
        <template v-else-if="showTtsGenerationWarning" #title>
          {{ ttsGenerationStatusText }}
        </template>
        <a-button
          block
          type="text"
          :disabled="!readyToPlay || loadingPlay || loadingPlayAux || !canPlayByTtsGeneration"
          @click="handleLaunchStrategy(false)"
          class="table-cell__actions-btn"
        >
          <CaretRightOutlined v-if="!loadingPlay && !loadingPlayAux" />
          <div v-else-if="loadingPlayAux" class="loading-icon">
            <CaretRightOutlined class="inner-icon" v-if="waitingToPlay" />
            <PauseOutlined class="inner-icon" v-else-if="waitingToPause" />
            <div class="spinner-circle"></div>
          </div>
          <LoadingOutlined v-else />
        </a-button>
      </a-tooltip>
    </a-tooltip>
  </div>

  <UsersInActiveStrategyModal
    :visible="usersInActiveStrategyModal.visible"
    :data="usersInActiveStrategyModal.data"
    @close="handleClose"
  />

  <RestrictionChangePlanModal
    title="¡Alcanzaste el límite de tu plan! Personalízalo y continúa tu viaje estelar."
    v-model:open="openRestriction"
    showAlert
  >
    <template #body>
      <p>
        Tu plan {{ PlanTypeText[plan] }} permite hasta 
        <span style="font-weight: 500">
          {{ maxConcurrentStrategy }} estrategias concurrentes.
        </span>
        Solicita personalizar tu plan para potenciar tu ambiente de trabajo.
      </p>
      <p>
        {{
          isClientBeexcc 
          ? "Cuenta con nosotros para elevar tu experiencia. Estamos listos para asistirte. ✨"
          : "Aprovecha al máximo todo lo que puedes lograr con BeexCC. ¡Lleva tu experiencia más lejos! ✨"
        }}
      </p>
    </template>
  </RestrictionChangePlanModal>

  <StrategyGenerateTtsAudiosModal
    v-model:open="generateTtsAudiosModalOpen"
    :strategy="strategy"
    :is-ivr-return="isIvrReturn"
    @generated="handleTtsAudiosGenerated"
  />
</template>

<script>
import { StrategyType } from "@/app/shared/utils/enums";
import {
  PauseOutlined,
  CaretRightOutlined,
  LoadingOutlined,
  SyncOutlined,
} from "@ant-design/icons-vue";
import { message, Modal } from "ant-design-vue";
import { LaunchErrorType } from "@/app/strategies/utils/enums";

import UsersInActiveStrategyModal from "@/app/strategies/components/modals/UsersInActiveStrategyModal.vue";
import StrategyGenerateTtsAudiosModal from "@/app/strategies/components/modals/StrategyGenerateTtsAudiosModal.vue";
import { h, ref, watch, inject, onMounted, onUnmounted, computed, createVNode } from "vue";
// import { mapIcon } from "@/app/notifications/utils/icons";
// import { h } from "vue";
import { PlanTypeText } from "@/app/common/types";
import RestrictionChangePlanModal from "@/app/common/components/modals/RestrictionChangePlanModal.vue";
import { useStore } from "vuex";
import dayjs from "dayjs";
import SyncTTSAudiosIcon from "@/app/shared/components/icons/SyncTTSAudiosIcon.vue";
import { StrategyTTSGenerationStatus } from "../../utils/modalStrategy";

export default {
  components: {
    PauseOutlined,
    CaretRightOutlined,
    LoadingOutlined,
    SyncOutlined,
    SyncTTSAudiosIcon,
    UsersInActiveStrategyModal,
    RestrictionChangePlanModal,
    StrategyGenerateTtsAudiosModal,
  },
  props: {
    strategy: {
      required: true,
      type: Object,
    },
    dataPlan: {
      required: true,
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const isPlay = ref(false);
    const loadingPlay = ref(false);
    const loadingPlayAux = ref(true);
    const lastAction = ref();

    const fetchTableData = inject("fetchTableData");

    const store = useStore();
    const user = computed(() => store.getters["getDataUser"]);
    const plan = computed(() => user.value?.change_custom_plan?.plan);
    const isClientBeexcc = computed(() => user.value?.is_beexcc);
    const maxConcurrentStrategy = computed(() => {
      console.log(props.dataPlan, 'props.dataPlan');
      return props.dataPlan?.configuration?.max_concurrent_strategy?.value || 0;
    });
    const openRestriction = ref(false);

    watch(
      () => props.strategy.turned_on,
      (newTurnedStatus) => {
        isPlay.value = newTurnedStatus;
        // loadingPlay.value = false;
      }
    );

    let timer;
    onMounted(async () => {
      timer = setInterval(() => {
        const currentDate = new Date(); // Actualiza cada segundo
        const dateLaunched = new Date(new Date(props.strategy.date_launched).getTime() + 5000); // Fecha de lanzamiento + 5 segundos
        const datePaused = new Date(new Date(props.strategy.paused_at).getTime() + 5000); // Fecha de lanzamiento + 5 segundos

        let shouldBeLoading
        if (dateLaunched > datePaused) {
          shouldBeLoading = currentDate <= dateLaunched;
        } else {
          shouldBeLoading = currentDate <= datePaused;
        }
        // console.log(shouldBeLoading, 'shouldBeLoading');
        if (shouldBeLoading != loadingPlayAux.value) {
          // console.log('cambia valor');
          loadingPlayAux.value = shouldBeLoading;
        }
      }, 1000);
    });

    const waitingToPause = computed(() => 
      lastAction.value ? lastAction.value == 'L' : props.strategy.date_launched > props.strategy.paused_at
    );
    const waitingToPlay = computed(() => 
      lastAction.value ? lastAction.value == 'F' : props.strategy.date_launched < props.strategy.paused_at
    );

    onUnmounted(() => {
      clearInterval(timer); // Limpia el temporizador al desmontar el componente
    });

    return {
      isPlay,
      loadingPlay,
      fetchTableData,
      loadingPlayAux,
      lastAction,
      waitingToPause,
      waitingToPlay,
      openRestriction,
      PlanTypeText,
      plan,
      maxConcurrentStrategy,
    };
  },
  data() {
    return {
      // InsufficientBalance: ref(false),
      usersInActiveStrategyModal: {
        visible: false,
        data: [],
      },
      generateTtsAudiosModalOpen: false,
      dayjs,
      StrategyTTSGenerationStatus,
    };
  },
  computed: {
    readyToPlay() {
      const isCompleted = this.strategy?.remaining_phones === this.strategy?.total_phones;
      const isIvr = this.strategy?.type_strategy === StrategyType.ivr;
      const isEmail = this.strategy?.type_strategy === StrategyType.email;
      const isPredictive = this.strategy?.type_strategy === StrategyType.predictive;

      if (isIvr) return this.strategy?.file_ivr && !isCompleted;
      if (isEmail && this.showEmailTime === 0) return !isCompleted;
      if (isPredictive && this.showEmailTime !== 0)
        return (this.strategy?.date_launched ? new Date(this.showEmailTime) < new Date() : false) && !isCompleted;
      
      return (

        !isCompleted &&
        !this.showAddAgentsWarning &&
        !this.showSetupEmailWarning &&
        !this.showSetupQueuesWarning &&
        !this.showNCEquivalenciesWarning &&
        this.portfolioHasDialingPlan
      );
    },
    ttsDetail() {
      return this.strategy?.tts_detail ?? null;
    },
    ttsGenerationStatus() {
      return this.ttsDetail?.generation_status;
    },
    isTtsStrategy() {
      return this.ttsDetail !== null;
    },
    isTtsGenerating() {
      return this.ttsGenerationStatus === StrategyTTSGenerationStatus.Generating;
    },
    showGenerateTtsAudiosButton() {
      return this.ttsDetail !== null &&
        this.ttsGenerationStatus === StrategyTTSGenerationStatus.Pending;
    },
    isTtsGenerationReady() {
      return !this.isTtsStrategy ||
        this.ttsGenerationStatus === StrategyTTSGenerationStatus.Ready;
    },
    ttsProcessedRecords() {
      return Number(this.ttsDetail?.processed_records || 0);
    },
    ttsTotalRecords() {
      return Number(this.ttsDetail?.total_records || 0);
    },
    ttsPendingRecords() {
      return Math.max(this.ttsTotalRecords - this.ttsProcessedRecords, 0);
    },
    ttsGenerationPercent() {
      if (!this.ttsTotalRecords) return 0;
      return Math.min(
        Math.round((this.ttsProcessedRecords / this.ttsTotalRecords) * 100),
        100
      );
    },
    formattedTtsTotalRecords() {
      return new Intl.NumberFormat("en-US").format(this.ttsTotalRecords);
    },
    formattedTtsPendingRecords() {
      return new Intl.NumberFormat("en-US").format(this.ttsPendingRecords);
    },
    formattedTtsEstimatedTimeLeft() {
      const secondsLeft = Number(this.ttsDetail?.estimated_seconds_left);

      if (!Number.isFinite(secondsLeft) || secondsLeft < 0) return "";
      if (secondsLeft < 60) {
        const roundedSecondsLeft = Math.ceil(secondsLeft);
        return `${roundedSecondsLeft} ${roundedSecondsLeft === 1 ? "segundo" : "segundos"}`;
      }

      const minutesLeft = Math.ceil(secondsLeft / 60);
      return `${minutesLeft} ${minutesLeft === 1 ? "minuto" : "minutos"}`;
    },
    showGenerateTtsAudiosButton() {
      return this.ttsDetail !== null && this.ttsGenerationStatus === StrategyTTSGenerationStatus.Pending;
    },
    canPlayByTtsGeneration() {
      return this.isTurnedOn || this.isTtsGenerationReady;
    },
    showTtsGenerationWarning() {
      return this.isTtsStrategy && !this.isTtsGenerationReady && !this.showGenerateTtsAudiosButton;
    },
    ttsGenerationStatusText() {
      const texts = {
        [StrategyTTSGenerationStatus.Generating]: "Los audios de esta estrategia se están generando.",
        [StrategyTTSGenerationStatus.Expired]: "Los audios de esta estrategia expiraron.",
        [StrategyTTSGenerationStatus.Failed]: "No se pudieron generar los audios de esta estrategia.",
        [StrategyTTSGenerationStatus.Pending]: "Primero debes generar los audios para lanzar esta estrategia.",
      };

      return texts[this.ttsGenerationStatus] || "Los audios de esta estrategia aún no están listos.";
    },
    portfolioHasDialingPlan() {
      return this.isMessaging ? true : this.strategy.dialing_plan_id;
    },
    isIvr() {
      return this.strategy.type_strategy == StrategyType.ivr;
    },
    isIvrReturn() {
      return this.strategy.type_strategy == StrategyType.ivrReturn;
    },
    isEmail() {
      return this.strategy.type_strategy == StrategyType.email;
    },
    isPredictive() {
      return this.strategy.type_strategy == StrategyType.predictive;
    },
    isMessaging() {
      const isSms = this.strategy.type_strategy == StrategyType.sms;
      const isWhatsapp = this.strategy.type_strategy == StrategyType.whatsapp;
      return isSms || isWhatsapp;
    },
    showAddAgentsWarning() {
      if (this.isMessaging || this.isIvr) return false;
      return !this.hasAgents;
    },
    // showSetupIvrWarning() {
    //   return this.isIvr;
    // },
    showSetupQueuesWarning() {
      if (!this.isMessaging) return false;
      return !this.isMessaging;
    },
    hasAgents() {
      return this.strategy.users.length > 0;
    },
    isTurnedOn() {
      return this.strategy.turned_on || this.isPlay;
    },
    hasTypificationDefault() {
      return this.strategy.default_typification;
    },
    showEmailTime() {
      return this.strategy.execution_time || 0;
    },
    user() {
      return this.$store.getters["getDataUser"];
    },
    showNCEquivalenciesWarning() {
      const isPredictive = this.strategy.type_strategy === StrategyType.predictive;
      const equivalencies = this.strategy.equivalences_nc;

      return isPredictive
        ? !(equivalencies && equivalencies?.every((equivalency) => equivalency?.result))
        : false;
    },
    isStrategyFinalized() {
      const progress = this.strategy?.remaining_phones - this.strategy?.total_phones;
      return progress === 0;
    },
    isProgramed() {
      return this.showEmailTime && 
        new Date(this.showEmailTime) > new Date() && 
        !this.readyToPlay 
        // && 
        // !this.isStrategyFinalized;
    },
    scheduleNotExecuted() {
      return this.showEmailTime && 
        new Date(this.showEmailTime) < new Date() && 
        !this.readyToPlay &&
        !this.strategy?.date_launched;
    }
  },
  methods: {
    handleOpenGenerateAudiosModal() {
      this.generateTtsAudiosModalOpen = true;
    },
    async handleTtsAudiosGenerated() {
      await this.fetchTableData({ showLoader: false });
    },
    async handleLaunchStrategy(withoutUsers = false) {
      if (this.hasTypificationDefault) {
        this.loadingPlay = true;
        this.lastAction = 'L';
        // this.InsufficientBalance = false;
        try {
          await this.$store.dispatch("launchStrategyById", { strategyId: this.strategy.id, withoutUsers });
          await this.fetchTableData({ showLoader: false });
          this.isPlay = true;
          setTimeout(() => {
            this.loadingPlay = false;
          }, 1000);
        } catch (error) {
          this.loadingPlay = false;
          if (error?.data?.users) {
            Modal.confirm({
              title: "Ahora mismo no hay usuarios disponibles en tu estrategia para atender las llamadas, ¿deseas iniciarla de todos modos?",
              centered: true,
              okText: "Sí, iniciar estrategia",
              cancelText: "No, Cancelar",
              onOk: () => {
                this.handleLaunchStrategy(true);
              },
            });
            return;
          }
          
          console.log(error?.data?.strategy_limit?.[0], 'error launch strategy');
          if (error?.data?.strategy_limit?.[0] == 'True') {
            this.openRestriction = true;
          } else if (error?.data?.code === LaunchErrorType.UserOnCurrentLaunched) {
            this.usersInActiveStrategyModal.visible = true;
            this.usersInActiveStrategyModal.data = error?.data?.users;
          } else if (error?.data?.code === LaunchErrorType.WabaAlreadyLaunched) {
            Modal.error({
              title: "Ya cuentas con otra estrategia de WhatsApp activa",
              content:
                "Actualmente, no es posible lanzar estrategias simultáneas con el mismo identificador. Por favor, completa o pausa una estrategia antes de iniciar otra.",
              centered: true,
            });
          } else if (error?.data?.code === LaunchErrorType.TierLimit) {
            Modal.error({
              title: () =>
                h("span", [
                  "Plantillas disponibles insuficientes según tu ",
                  h(
                    "a",
                    {
                      href: "https://developers.facebook.com/docs/whatsapp/messaging-limits/",
                      target: "_blank",
                    },
                    "TIER"
                  ),
                ]),
              content:
                "No hemos podido lanzar la estrategia debido a que la cantidad de teléfonos identificados supera el número de plantillas disponibles a enviar según tu Tier.",
              centered: true,
            });
          // } else if (error?.getErrorMessage?.() === LaunchErrorType.InsufficientBalance) {
        } else if (error?.data?.code === LaunchErrorType.UsersValidation) {
           Modal.error({
              title: "Los siguientes usuarios están asignados a otra estrategia activa:",
              content: createVNode(
                "ul",
                null,
                error?.data?.users_validate?.map((user) => h("li", null, user))
              ),
              centered: true,
            });
        } else if (error?.turned_on?.length > 0) {
            message.error(error?.turned_on[0]);
            // this.InsufficientBalance = true;
            // console.log("turned_on:No cuenta con saldo para lanzar una estrategia de SMS");
            // console.log("Parte 1: " + error?.data?.turned_on);
          } else {
            const errorMessage =
              error?.getErrorMessage?.() || "Algo salió mal, por favor, vuelve a intentarlo";
            message.error(errorMessage);
            console.error(error);
          }
          this.isPlay = false;
        }
      } else {
        this.$confirm({
          title: "Actualmente no tienes una tipificación por defecto",
          content:
            "Ve a la sección de campañas y agrega una tipificación por defecto para poder continuar",
          centered: true,
          okText: "Entendido",
          cancelText: "Cancelar",
        });
      }
    },
    async handleFinishStrategy() {
      this.loadingPlay = true;
      this.lastAction = 'F'
      try {
        await this.$store.dispatch("finishStrategyById", this.strategy.id);
        await this.fetchTableData({ showLoader: false });
        this.isPlay = false;
        setTimeout(() => {
          this.loadingPlay = false;
        }, 1000);
      } catch (error) {
        message.warning("Algo salió mal, por favor, vuelve a intentarlo");
        console.error(error);
        // this.isPlay = false;
        this.loadingPlay = false;
      }
    },
    handleClose() {
      this.usersInActiveStrategyModal.visible = false;
    },
    // InsufficientBalance2() {
    //   console.log("InsufficientBalance: " + this.InsufficientBalance);
    //   return this.InsufficientBalance
    //     ? "No cuenta con saldo suficiente para realizar esta estrategia."
    //     : "Comenzar";
    // },
    showTooltipSms() {
      return this.strategy.type_strategy == StrategyType.sms &&
      this.strategy.sms_balance?.sms_type_billing == 1 &&
      this.strategy.sms_balance?.sms_balance <= 0
    },
  },
};
</script>

<style lang="sass" scoped>
.strategy-play-btn .ant-btn
  padding: 5px
  display: flex
  flex-direction: column
  justify-content: center
  .anticon
    font-size: 16px

.loading-icon
  position: relative
  display: flex
  align-items: center
  width: 24px
  height: 24px
  left: -4px

.inner-icon
  position: absolute
  top: 50%
  left: 50%
  transform: translate(-50%, -50%)
  font-size: 24px
  color: #909090

.spinner-circle
  width: 24px
  height: 24px
  // border: 1px solid rgba(24, 144, 255, 0.3)
  border-top: 1px solid #909090
  border-radius: 60%
  animation: spin 1s linear infinite

.tts-generation-popover
  min-width: 320px

  h4
    margin: 0 0 12px
    font-weight: 600
    color: $gray-9

  p
    margin: 0 0 6px
    color: $gray-8

.tts-generation-progress
  display: flex
  align-items: center
  gap: 12px

  .ant-progress
    flex: 1

.tts-generation-time
  margin-top: 6px
  color: $gray-7

.tts-generating-btn
  cursor: default

.tts-generating-spinner
  width: 18px
  height: 18px
  border: 3px solid rgba(24, 144, 255, 0.18)
  border-top-color: #1890ff
  border-radius: 50%
  animation: tts-spin 0.8s linear infinite

@keyframes tts-spin
  0%
    transform: rotate(0deg)
  100%
    transform: rotate(360deg)

/* Animación de giro */
@keyframes spin
  0%
    transform: rotate(0deg)
  100%
    transform: rotate(360deg)

</style>
